import { SystemTypes } from 'spark-core-dx/services';

export const CLIENT_COMMUNICATION_ITEM_NAMES = Object.freeze({
  workspace: 'ClientCommunicationsWorkspace',
  configuration: 'ClientCommunicationConfiguration',
  providerAction: 'ClientCommunicationProviderAction',
  notificationRequest: 'NotificationRequest',
});

export const CLIENT_COMMUNICATION_OPERATIONS = Object.freeze({
  loadWorkspace: 'LoadClientCommunications',
  loadTool: 'LoadClientCommunicationTool',
  selectProviders: 'SelectClientCommunicationProviders',
  saveConfiguration: 'SaveClientCommunicationConfiguration',
  clearOverride: 'ClearClientCommunicationOverride',
  validateProvider: 'ValidateClientCommunicationProvider',
  testProvider: 'TestClientCommunicationProvider',
  saveToolItem: 'SaveClientCommunicationToolItem',
  removeToolItem: 'RemoveClientCommunicationToolItem',
  searchNotificationRecipients: 'SearchClientNotificationRecipients',
  saveInternalNotificationDraft: 'SaveClientInternalNotificationDraft',
  dispatchInternalNotification: 'DispatchClientInternalNotification',
  loadCommunicationOperationStatus: 'LoadClientCommunicationOperationStatus',
  aiQuestionManage: 'AIQuestionClientCommunicationManage',
});

export const CLIENT_COMMUNICATION_PROVIDER_CARD_METADATA = Object.freeze({
  Name: 'ClientCommunicationProvider',
  DisplayName: 'Communication Providers',
  Fields: [
    {
      FieldName: 'DisplayName',
      DisplayName: 'Provider',
      PrimaryField: true,
      ShowOnCard: true,
      GridSequence: 1,
    },
    {
      FieldName: 'Channel',
      DisplayName: 'Channel',
      ShowOnCard: true,
      GridSequence: 2,
    },
    {
      FieldName: 'Description',
      DisplayName: 'Description',
      ShowOnCard: true,
      GridSequence: 3,
    },
  ],
});

export const CLIENT_COMMUNICATION_CONFIGURATION_FIELDS = Object.freeze([
  'SendGridFromEmail',
  'SendGridEmailAPIKeyInput',
  'TwilioAccountSIDInput',
  'TwilioKeySIDInput',
  'TwilioPhoneKeySecretInput',
  'TwilioFromPhone',
]);

export const CLIENT_COMMUNICATION_TEST_FIELDS = Object.freeze([
  'TestRecipient',
  'TestSubject',
  'TestMessage',
]);

export const CLIENT_COMMUNICATION_SEARCH_FIELD = Object.freeze({
  FieldName: 'SearchText',
  DisplayName: 'Search this tool',
  Description: 'Server-side search within the selected client and application.',
  FieldType: 'TextBox',
  Format: 'string',
});

export const EMPTY_NOTIFICATION_FILTER = Object.freeze({
  SearchText: '',
  NotificationTypes: [],
  RequestStatuses: [],
  RecipientStatuses: [],
  StartDateFrom: null,
  StartDateTo: null,
  DeliveryTiming: '',
});

export const EMPTY_NOTIFICATION_LOG_FILTER = Object.freeze({
  SearchText: '',
  NotificationTypes: [],
  NotificationStatuses: [],
  StartDateFrom: null,
  StartDateTo: null,
  ReadState: '',
  TestState: '',
});

const createSubmissionKey = () => {
  if (globalThis.crypto?.randomUUID) return globalThis.crypto.randomUUID();
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (character) => {
    const random = Math.floor(Math.random() * 16);
    const value = character === 'x' ? random : ((random & 0x3) | 0x8);
    return value.toString(16);
  });
};

export const createInternalNotificationDraft = (overrides = {}) => ({
  SubmissionKey: createSubmissionKey(),
  RequestId: null,
  SourceRequestId: null,
  Action: 'Create',
  RecipientProfileIds: [],
  NotificationType: 1,
  Title: '',
  Message: '',
  SendNow: true,
  StartDate: null,
  EndDate: null,
  ...overrides,
});

export const INTERNAL_NOTIFICATION_ITEM_FIELD_NAMES = Object.freeze([
  'NotificationType',
  'Title',
  'Message',
  'StartDate',
  'EndDate',
]);

export const INTERNAL_NOTIFICATION_SEND_NOW_FIELD = Object.freeze({
  FieldName: 'SendNow',
  DisplayName: 'Send now',
  Description: 'Clear this option to schedule delivery.',
  FieldType: SystemTypes.UIFieldType.CheckBox,
  DisplaySequence: 4,
});

export const INTERNAL_NOTIFICATION_RECIPIENT_METADATA = Object.freeze({
  Name: 'ClientNotificationRecipient',
  DisplayName: 'Users',
  Fields: [
    {
      FieldName: 'DisplayName',
      DisplayName: 'Name',
      PrimaryField: true,
      ShowOnCard: true,
      GridSequence: 1,
    },
    {
      FieldName: 'Email',
      DisplayName: 'Email',
      ShowOnCard: true,
      GridSequence: 2,
    },
  ],
});

const NOTIFICATION_AI_DRAFT_FIELDS = new Set([
  'notificationtype', 'title', 'message', 'recipientemails',
]);
const NOTIFICATION_DRAFT_CONTEXT_FIELDS = new Set([
  'notificationtype', 'title', 'message',
]);
const NOTIFICATION_FILTER_FIELDS = new Set(
  Object.keys(EMPTY_NOTIFICATION_FILTER).map((key) => key.toLowerCase()),
);
const NOTIFICATION_LOG_FILTER_FIELDS = new Set(
  Object.keys(EMPTY_NOTIFICATION_LOG_FILTER).map((key) => key.toLowerCase()),
);

const normalizeNotificationFieldValue = (fieldName, value, label) => {
  if (fieldName === 'NotificationType') {
    const namedTypes = { alert: 0, standard: 1, important: 2 };
    const numeric = typeof value === 'string' && namedTypes[value.trim().toLowerCase()] != null
      ? namedTypes[value.trim().toLowerCase()]
      : Number(value);
    if (!Number.isInteger(numeric) || numeric < 0 || numeric > 2) {
      throw new Error(`${label} notification type is invalid.`);
    }
    return numeric;
  }
  if (fieldName === 'NotificationTypes') {
    if (!Array.isArray(value)) throw new Error(`${label} notification types must be an array.`);
    return [...new Set(value.map((entry) => (
      normalizeNotificationFieldValue('NotificationType', entry, label)
    )))];
  }
  if (fieldName === 'NotificationStatuses') {
    if (!Array.isArray(value) || value.some((entry) => typeof entry !== 'string')) {
      throw new Error(`${label} notification statuses must be an array of names.`);
    }
    const allowedStatuses = new Set(['pending', 'displayed', 'dismissed']);
    const statuses = [...new Set(value.map((entry) => entry.trim().toLowerCase()).filter(Boolean))];
    if (statuses.some((entry) => !allowedStatuses.has(entry))) {
      throw new Error(`${label} contains an invalid notification status.`);
    }
    return statuses;
  }
  if (fieldName === 'RequestStatuses' || fieldName === 'RecipientStatuses') {
    if (!Array.isArray(value) || value.some((entry) => typeof entry !== 'string')) {
      throw new Error(`${label} statuses must be an array of names.`);
    }
    const allowedStatuses = fieldName === 'RequestStatuses'
      ? new Set(['draft', 'scheduled', 'live', 'completed', 'failed', 'expired', 'cancelled'])
      : new Set(['pending', 'displayed', 'dismissed']);
    const statuses = [...new Set(value.map((entry) => entry.trim().toLowerCase()).filter(Boolean))];
    if (statuses.some((entry) => !allowedStatuses.has(entry))) {
      throw new Error(`${label} contains an invalid status.`);
    }
    return statuses;
  }
  if (fieldName === 'SendNow') {
    if (typeof value !== 'boolean') throw new Error(`${label} SendNow must be true or false.`);
    return value;
  }
  if (['StartDate', 'EndDate', 'StartDateFrom', 'StartDateTo'].includes(fieldName)) {
    if (value == null || value === '') return null;
    if (typeof value !== 'string' || Number.isNaN(Date.parse(value))) {
      throw new Error(`${label} ${fieldName} must be a valid date and time.`);
    }
    return value;
  }
  if (fieldName === 'DeliveryTiming') {
    if (typeof value !== 'string') throw new Error(`${label} delivery timing must be text.`);
    const timing = value.trim().toLowerCase();
    if (!['', 'immediate', 'scheduled'].includes(timing)) {
      throw new Error(`${label} delivery timing is invalid.`);
    }
    return timing;
  }
  if (fieldName === 'ReadState') {
    if (typeof value !== 'string') throw new Error(`${label} read state must be text.`);
    const state = value.trim().toLowerCase();
    if (!['', 'read', 'unread'].includes(state)) {
      throw new Error(`${label} read state is invalid.`);
    }
    return state;
  }
  if (fieldName === 'TestState') {
    if (typeof value !== 'string') throw new Error(`${label} test state must be text.`);
    const state = value.trim().toLowerCase();
    if (!['', 'test', 'production'].includes(state)) {
      throw new Error(`${label} test state is invalid.`);
    }
    return state;
  }
  if (['SearchText', 'Title', 'Message'].includes(fieldName)) {
    if (typeof value !== 'string') throw new Error(`${label} ${fieldName} must be text.`);
    return value;
  }
  if (fieldName === 'RecipientEmails') {
    if (!Array.isArray(value) || value.some((entry) => typeof entry !== 'string')) {
      throw new Error(`${label} recipient emails must be an array of email addresses.`);
    }
    const emails = [...new Set(value.map((entry) => entry.trim().toLowerCase()).filter(Boolean))];
    if (emails.some((email) => !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email))) {
      throw new Error(`${label} contains an invalid recipient email address.`);
    }
    return emails;
  }
  return cloneCommunicationValue(value);
};

const normalizeAllowlistedObject = (value, allowed, label) => {
  let source = value;
  if (typeof source === 'string') {
    try {
      source = JSON.parse(source);
    } catch {
      throw new Error(`${label} must be valid JSON.`);
    }
  }
  const wrapper = label.toLowerCase().includes('filter')
    ? source?.Filter || source?.filter || source?.Value || source?.value
    : source?.Draft || source?.draft || source?.Value || source?.value;
  if (wrapper && typeof wrapper === 'object' && !Array.isArray(wrapper)) source = wrapper;
  if (!source || typeof source !== 'object' || Array.isArray(source)) {
    throw new Error(`${label} must be an object.`);
  }
  return Object.entries(source).reduce((result, [key, fieldValue]) => {
    const normalizedKey = String(key).toLowerCase();
    if (!allowed.has(normalizedKey)) {
      throw new Error(`${label} field '${key}' is not supported.`);
    }
    const canonicalKey = [...allowed].find((candidate) => candidate === normalizedKey);
    const canonicalNames = {
      notificationtype: 'NotificationType',
      title: 'Title',
      message: 'Message',
      recipientemails: 'RecipientEmails',
      sendnow: 'SendNow',
      startdate: 'StartDate',
      enddate: 'EndDate',
      searchtext: 'SearchText',
      notificationtypes: 'NotificationTypes',
      requeststatuses: 'RequestStatuses',
      recipientstatuses: 'RecipientStatuses',
      startdatefrom: 'StartDateFrom',
      startdateto: 'StartDateTo',
      deliverytiming: 'DeliveryTiming',
      notificationstatuses: 'NotificationStatuses',
      readstate: 'ReadState',
      teststate: 'TestState',
    };
    const canonicalName = canonicalNames[canonicalKey] || key;
    result[canonicalName] = normalizeNotificationFieldValue(
      canonicalName,
      fieldValue,
      label,
    );
    return result;
  }, {});
};

export const normalizeNotificationAIDraft = (value) => (
  normalizeAllowlistedObject(value, NOTIFICATION_AI_DRAFT_FIELDS, 'Notification draft')
);

export const buildSafeNotificationDraftAIPayload = (record) => {
  if (!record || typeof record !== 'object' || Array.isArray(record)) return {};
  const draft = Object.entries(record).reduce((result, [key, value]) => {
    if (NOTIFICATION_DRAFT_CONTEXT_FIELDS.has(String(key).toLowerCase())) {
      result[key] = value;
    }
    return result;
  }, {});
  return normalizeNotificationAIDraft(draft);
};

export const resolveNotificationRecipientEmails = (recipientEmails, recipients = []) => {
  const normalizedEmails = normalizeNotificationFieldValue(
    'RecipientEmails',
    recipientEmails || [],
    'Notification draft',
  );
  const eligibleByEmail = new Map(
    (recipients || [])
      .filter((recipient) => recipient?.IsActive !== false && recipient?.ProfileId)
      .map((recipient) => [
        String(recipient.Email || '').trim().toLowerCase(),
        String(recipient.ProfileId),
      ])
      .filter(([email]) => email),
  );
  return {
    RecipientProfileIds: normalizedEmails
      .filter((email) => eligibleByEmail.has(email))
      .map((email) => eligibleByEmail.get(email)),
    UnmatchedRecipientEmails: normalizedEmails
      .filter((email) => !eligibleByEmail.has(email)),
  };
};

export const normalizeNotificationFilter = (value = {}) => ({
  ...EMPTY_NOTIFICATION_FILTER,
  ...normalizeAllowlistedObject(value, NOTIFICATION_FILTER_FIELDS, 'Notification filter'),
});

export const normalizeNotificationLogFilter = (value = {}) => ({
  ...EMPTY_NOTIFICATION_LOG_FILTER,
  ...normalizeAllowlistedObject(
    value,
    NOTIFICATION_LOG_FILTER_FIELDS,
    'Notifications Log filter',
  ),
});

export const notificationFilterChips = (filter = EMPTY_NOTIFICATION_FILTER) => {
  const chips = [];
  if (filter.SearchText) chips.push(`Search: ${filter.SearchText}`);
  if (filter.NotificationTypes?.length) chips.push(`Types: ${filter.NotificationTypes.join(', ')}`);
  if (filter.RequestStatuses?.length) chips.push(`Status: ${filter.RequestStatuses.join(', ')}`);
  if (filter.RecipientStatuses?.length) chips.push(`Recipient: ${filter.RecipientStatuses.join(', ')}`);
  if (filter.StartDateFrom || filter.StartDateTo) chips.push('Date range');
  if (filter.DeliveryTiming) chips.push(`Delivery: ${filter.DeliveryTiming}`);
  return chips;
};

export const notificationLogFilterChips = (
  filter = EMPTY_NOTIFICATION_LOG_FILTER,
) => {
  const chips = [];
  if (filter.SearchText) chips.push(`Search: ${filter.SearchText}`);
  if (filter.NotificationTypes?.length) chips.push(`Types: ${filter.NotificationTypes.join(', ')}`);
  if (filter.NotificationStatuses?.length) chips.push(`Status: ${filter.NotificationStatuses.join(', ')}`);
  if (filter.StartDateFrom || filter.StartDateTo) chips.push('Date range');
  if (filter.ReadState) chips.push(`Read: ${filter.ReadState}`);
  if (filter.TestState) chips.push(`Source: ${filter.TestState}`);
  return chips;
};

const AI_DRAFT_FIELDS = new Set([
  'name',
  'description',
  'title',
  'subject',
  'body',
  'message',
  'bodytemplate',
  'subjecttemplate',
  'templatekeycategory',
  'bodytemplatekeys',
  'subjecttemplatekeys',
  'ishtml',
  'notificationtype',
  'sendasemail',
  'sendassms',
  'notifyapp',
  'sendnow',
  'startdate',
  'enddate',
]);

export const cloneCommunicationValue = (value) => {
  if (value == null) return value;
  return JSON.parse(JSON.stringify(value));
};

export const communicationRecordId = (record) => (
  record?._id || record?.UniqueId || record?.uniqueId || ''
);

export const communicationRecordVersion = (record) => (
  record?.ConcurrencyToken || record?.concurrencyToken || ''
);

export const selectCommunicationFields = (metadata, fieldNames) => {
  const allowed = new Set(fieldNames.map((name) => name.toLowerCase()));
  return (metadata?.Fields || metadata?.fields || []).filter((field) => (
    allowed.has(String(field?.FieldName || field?.fieldName || '').toLowerCase())
  ));
};

export const createCommunicationRequest = ({
  workspace,
  clientId,
  appId,
  currentEnvironment,
  overrides = {},
}) => ({
  ...(workspace || {}),
  ClientId: clientId,
  ApplicationId: appId,
  CurrentEnvironment: currentEnvironment,
  RecordJson: '',
  RecordId: '',
  ExpectedRecordVersion: '',
  OverrideKeys: [],
  ...overrides,
});

export const buildSafeCommunicationAIPayload = (record) => {
  if (!record || typeof record !== 'object') return {};
  return Object.entries(record).reduce((result, [key, value]) => {
    if (!AI_DRAFT_FIELDS.has(String(key).toLowerCase())) return result;
    if (typeof value === 'string') {
      result[key] = value.slice(0, 16000);
    } else if (
      typeof value === 'boolean'
      || typeof value === 'number'
      || Array.isArray(value)
    ) {
      result[key] = cloneCommunicationValue(value);
    }
    return result;
  }, {});
};

const NOTIFICATION_LOG_DIAGNOSTIC_FIELDS = new Set([
  'notificationtype',
  'title',
  'startdate',
  'enddate',
  'displayeddate',
  'dismisseddate',
  'isread',
  'ispinned',
  'istest',
  'isactive',
]);

export const buildSafeNotificationLogDiagnostics = (records = []) => (
  (Array.isArray(records) ? records : [])
    .slice(0, 50)
    .map((record) => Object.entries(record || {}).reduce((result, [key, value]) => {
      if (!NOTIFICATION_LOG_DIAGNOSTIC_FIELDS.has(String(key).toLowerCase())) return result;
      if (typeof value === 'string') result[key] = value.slice(0, 1000);
      else if (typeof value === 'boolean' || typeof value === 'number' || value == null) {
        result[key] = value;
      }
      return result;
    }, {}))
);

export const toolPageCount = (workspace) => Math.max(
  1,
  Math.ceil(
    Number(workspace?.TotalRecords || 0)
      / Math.max(1, Number(workspace?.PageSize || 50)),
  ),
);
